import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Property } from '@/mocks/properties';

interface CompareContextType {
  compareList: Property[];
  addToCompare: (property: Property) => void;
  removeFromCompare: (id: string) => void;
  clearCompare: () => void;
  isInCompare: (id: string) => boolean;
  canAdd: boolean;
}

const CompareContext = createContext<CompareContextType | null>(null);

export const CompareProvider = ({ children }: { children: ReactNode }) => {
  const [compareList, setCompareList] = useState<Property[]>([]);

  const addToCompare = useCallback((property: Property) => {
    setCompareList(prev => {
      if (prev.find(p => p.id === property.id)) return prev;
      if (prev.length >= 3) return prev;
      return [...prev, property];
    });
  }, []);

  const removeFromCompare = useCallback((id: string) => {
    setCompareList(prev => prev.filter(p => p.id !== id));
  }, []);

  const clearCompare = useCallback(() => {
    setCompareList([]);
  }, []);

  const isInCompare = useCallback((id: string) => {
    return compareList.some(p => p.id === id);
  }, [compareList]);

  const canAdd = compareList.length < 3;

  return (
    <CompareContext.Provider value={{ compareList, addToCompare, removeFromCompare, clearCompare, isInCompare, canAdd }}>
      {children}
    </CompareContext.Provider>
  );
};

export const useCompare = () => {
  const ctx = useContext(CompareContext);
  if (!ctx) throw new Error('useCompare must be used within CompareProvider');
  return ctx;
};
